import { useState, useEffect } from 'react';
import { livingPcfAPI } from '@/lib/api';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { Activity } from 'lucide-react';

export default function LivingPCF() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hidden, setHidden] = useState({});

  useEffect(() => {
    livingPcfAPI.getData().then(r => { setData(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  const toggleProduct = (name) => setHidden(prev => ({ ...prev, [name]: !prev[name] }));

  // Build unified chart data
  const chartData = [];
  if (data.length > 0) {
    const months = data[0]?.monthly_data || [];
    months.forEach((m) => {
      const point = { month: m.month };
      data.forEach(p => {
        const found = p.monthly_data.find(d => d.month === m.month);
        point[p.product_name] = found ? found.pcf_value : null;
      });
      chartData.push(point);
    });
  }

  // Last 6 months table
  const last6 = chartData.slice(-6);

  if (loading) return <div className="space-y-4"><div className="h-8 w-40 bg-muted rounded animate-pulse" /><div className="h-80 bg-muted rounded-xl animate-pulse" /></div>;

  return (
    <div data-testid="living-pcf-page" className="space-y-6">
      <div className="cp-page-header">
        <h1 className="cp-page-title flex items-center gap-2">
          <Activity className="w-6 h-6 text-primary" /> Living PCF
        </h1>
      </div>

      <div className="cp-chart-card animate-fade-in" data-testid="living-pcf-chart">
        <h3 className="text-sm font-heading font-semibold mb-4">PCF Trends (Last 12 Months)</h3>

        {/* Legend with toggle */}
        <div className="flex flex-wrap gap-3 mb-4">
          {data.map(p => (
            <button key={p.product_name} onClick={() => toggleProduct(p.product_name)}
              className={`flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full border transition-all ${hidden[p.product_name] ? 'opacity-40 border-border' : 'border-transparent'}`}
              style={{ background: hidden[p.product_name] ? 'transparent' : `${p.color}15`, color: p.color }}
              data-testid={`toggle-${p.product_name.replace(/\s+/g, '-').toLowerCase()}`}>
              <span className="w-2 h-2 rounded-full" style={{ background: p.color }} />
              {p.product_name}
            </button>
          ))}
        </div>

        <div className="h-72">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis dataKey="month" tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }} tickLine={false} axisLine={false} />
              <YAxis tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }} tickLine={false} axisLine={false} />
              <Tooltip contentStyle={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: 8, fontSize: 12 }} />
              {data.map(p => (
                !hidden[p.product_name] && (
                  <Line key={p.product_name} type="monotone" dataKey={p.product_name} stroke={p.color} strokeWidth={2} dot={{ r: 2 }} activeDot={{ r: 4 }} />
                )
              ))}
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Last 6 months table */}
      <div className="cp-chart-card animate-fade-in" data-testid="living-pcf-table">
        <h3 className="text-sm font-heading font-semibold mb-4">Last 6 Months Detail</h3>
        <div className="cp-table-wrapper overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Month</TableHead>
                {data.map(p => <TableHead key={p.product_name} className="text-right">{p.product_name}</TableHead>)}
              </TableRow>
            </TableHeader>
            <TableBody>
              {last6.map(row => (
                <TableRow key={row.month}>
                  <TableCell className="font-medium">{row.month}</TableCell>
                  {data.map(p => (
                    <TableCell key={p.product_name} className="text-right font-mono text-sm">
                      {row[p.product_name] || '-'}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
              {last6.length === 0 && (
                <TableRow><TableCell colSpan={data.length + 1} className="text-center py-8 text-muted-foreground">No data available</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
}
